<script setup lang="ts">
import Cookies from 'js-cookie'
import { storeToRefs } from 'pinia'
import { z } from 'zod'
import i18n from '@/locales'
import { useUserStore } from '@/stores/use-user-store'
import { CookiesKey } from '@library/enums/cookies-key'
import { useErrorMessageListStore } from '@library/stores/use-error-message-list-store'
import { BfVersion } from '~library/components'
import {
  useLibraryStyle,
  useLocale,
  useReloadSameRoute,
  useUserIdle
} from '~library/composables'
import { Locale } from '~library/enums'
import { useBfInjectStore, useGlobalStateStore } from '~library/stores'
import { isApiSuccess, isType } from '~library/utils/functions'
import IconRaw from '../reference/library/src/public/images/icons/icons-sprite.svg?component'
import { useAuth } from './composables/use-auth'
import { useReload } from './composables/use-reload'
import { api } from './services'
import { useIdentityStore } from './stores/use-identity-store'
import { useLoadingStore } from './stores/use-loading-store'
import { useStatusStore } from './stores/use-status-store'
import { deepClone } from './utils/functions/deep-clone'

const route = useRoute()
const router = useRouter()

const { initAuthData } = useAuth()

const { storageListenerReload } = useReload()
const { userProfile } = storeToRefs(useUserStore())
const { isSpinnerLoading } = storeToRefs(useLoadingStore())
const { setBfApi } = useBfInjectStore()
const { isUserLogin } = storeToRefs(useIdentityStore())

const { routerViewKey } = storeToRefs(useGlobalStateStore())

const { setErrorMessageList } = useErrorMessageListStore()

const { addLibraryStyle } = useLibraryStyle()

const layout = computed(() => {
  return route.meta.layout ?? ''
})

// composables
const { locale } = i18n.global
const { setLocaleValue, setLangAndBodyClass } = useLocale()
const { logout } = useAuth()
const { isApiChecked } = storeToRefs(useStatusStore())

useReloadSameRoute(route)
// 使用者閒置，自動登出
useUserIdle(idleAction)

function idleAction() {
  if (userProfile.value) {
    logout()
  }
}

// 網址有加 ?lang=語系，且系統支援該語系時，使用該語系，否則使用 zh
watch(
  () => route.query,
  () => {
    if (route.query.lang) {
      // 判斷符合語系型別時, 儲存 localStorage
      if (isType(route.query.lang, z.nativeEnum(Locale))) {
        setLocaleValue(route.query.lang, route.query)
      }

      // 隱藏 ?lang
      if (!route.query.preventStorageLocale) {
        const newQuery = deepClone(route.query)
        delete newQuery.lang

        // 網址取代為沒有帶 ?lang=語系 的網址
        router.replace({ query: newQuery })
      }
    }
  }
)

// 重寫 index.html 的 lang, body 的 font-class
watch(() => locale.value, setLangAndBodyClass, {
  immediate: true
})

onBeforeMount(async () => {
  initAuthData()
  storageListenerReload()
  setBfApi(api.bf)

  // 若為登入狀態且 Cookies DO_NOT_LOGOUT 變數為 null 則登出
  if (isUserLogin.value && !Cookies.get(CookiesKey.DO_NOT_LOGOUT)) {
    await logout()
  }
})

onMounted(async () => {
  addLibraryStyle()

  const responseData = (await api.bf.unAuth.getErrorMessageList()).data

  if (isApiSuccess(responseData)) {
    setErrorMessageList(responseData.data)
  }
})
</script>

<template>
  <noscript>
    <div style="float: left; color: #af3601; padding: 10px">
      {{ $t('Common.App.SorryForFeaturesOfJavaScript') }}
    </div>
  </noscript>

  <template v-if="isApiChecked">
    <component :is="layout" v-if="layout" :key="`${routerViewKey}-component`" />

    <router-view v-else :key="routerViewKey" />
  </template>

  <app-toast />

  <!-- 無延遲的 Loading 遮罩，避免呼叫 API 期間 app-modal 尚未顯示時按鈕被多次觸發 -->
  <div v-if="isSpinnerLoading" class="loader-wrap"></div>

  <!-- Spinners Loader -->
  <app-modal
    only-body
    :aria-describedby="'SpinnersLoader'"
    :closeable="false"
    :enable-transform="false"
    :focus="isSpinnerLoading"
    :modal-body-class="'bg-transparent'"
    :modal-class="'modal-loading'"
    :model-value="isSpinnerLoading"
    :size="'sm'"
  >
    <div class="loader-wrap loader-global show">
      <app-public-image alt="loader" src="images/loader_full.gif" />
    </div>
  </app-modal>

  <app-global-dialog />

  <bf-version />

  <icon-raw style="display: none" />
</template>

<style lang="scss" scoped>
.loader-wrap {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1000;
}
</style>
